/**
 * Theme Variant Transform
 *
 * Algorithmic light/medium/dark variants for every theme.
 * Ensures WCAG AA compliance across all generated palettes.
 */

import { ColorPalette } from '../types';
import { ThemeVariant } from './identifiers';

export { ThemeVariant };

// ---------------------------------------------------------------------------
// HSL helpers
// ---------------------------------------------------------------------------

/** Convert hex to [h(0-360), s(0-100), l(0-100)] */
export function hexToHSLValues(hex: string): [number, number, number] {
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(c => c + c)
      .join('');
  }
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

/** Convert [h(0-360), s(0-100), l(0-100)] to hex */
export function hslToHex(h: number, s: number, l: number): string {
  h = ((h % 360) + 360) % 360;
  s = Math.max(0, Math.min(100, s));
  l = Math.max(0, Math.min(100, l));

  const sRatio = s / 100;
  const lRatio = l / 100;
  const c = (1 - Math.abs(2 * lRatio - 1)) * sRatio;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lRatio - c / 2;

  let r = 0,
    g = 0,
    b = 0;
  if (h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Shift lightness by delta percentage points */
export function adjustLightness(hex: string, delta: number): string {
  const [h, s, l] = hexToHSLValues(hex);
  return hslToHex(h, s, Math.max(0, Math.min(100, l + delta)));
}

/** Set lightness to exact percentage */
export function setLightness(hex: string, l: number): string {
  const [h, s] = hexToHSLValues(hex);
  return hslToHex(h, s, Math.max(0, Math.min(100, l)));
}

// ---------------------------------------------------------------------------
// WCAG helpers
// ---------------------------------------------------------------------------

function linearize(v: number): number {
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

/** Relative luminance per WCAG 2.1 */
export function getRelativeLuminance(hex: string): number {
  hex = hex.replace('#', '');
  if (hex.length === 3)
    hex = hex
      .split('')
      .map(c => c + c)
      .join('');
  const r = linearize(parseInt(hex.substring(0, 2), 16) / 255);
  const g = linearize(parseInt(hex.substring(2, 4), 16) / 255);
  const b = linearize(parseInt(hex.substring(4, 6), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Contrast ratio per WCAG 2.1 */
export function getContrastRatio(hex1: string, hex2: string): number {
  const l1 = getRelativeLuminance(hex1);
  const l2 = getRelativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Adjust fg lightness (binary search) until it meets minRatio contrast against bg.
 * Moves toward white on dark backgrounds, toward black on light backgrounds.
 * Returns fg unchanged if it already passes.
 */
export function ensureMinContrast(
  fg: string,
  bg: string,
  minRatio: number
): string {
  if (getContrastRatio(fg, bg) >= minRatio) return fg;

  const bgLum = getRelativeLuminance(bg);
  const [h, s, l] = hexToHSLValues(fg);

  // Dark bg → lighten fg; light bg → darken fg
  const isDarkBg = bgLum < 0.18;

  let lo: number, hi: number;
  if (isDarkBg) {
    lo = l;
    hi = 100;
  } else {
    lo = 0;
    hi = l;
  }

  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    const candidate = hslToHex(h, s, mid);
    const passes = getContrastRatio(candidate, bg) >= minRatio;

    if (isDarkBg) {
      // Find minimum l that passes → if passes, try darker (lower hi)
      if (passes) hi = mid;
      else lo = mid;
    } else {
      // Find maximum l that passes → if passes, try lighter (raise lo)
      if (passes) lo = mid;
      else hi = mid;
    }
  }

  return hslToHex(h, s, isDarkBg ? hi : lo);
}

// ---------------------------------------------------------------------------
// Medium variant
// ---------------------------------------------------------------------------

/**
 * Medium variant: darken all surface/background colors by 22 lightness points,
 * then ensure ALL foreground elements (text, borders, icons, accents) pass
 * WCAG AA against the new, darker page background.
 */
export function applyMediumVariant(palette: ColorPalette): ColorPalette {
  const DARK_DELTA = -22;

  const newBg = adjustLightness(palette.background, DARK_DELTA);
  const newOffWhite = adjustLightness(palette.offWhite, DARK_DELTA);
  const newDisabledBg = adjustLightness(
    palette.disabled.background,
    DARK_DELTA
  );
  const newWarningBg = adjustLightness(palette.warning.background, DARK_DELTA);
  const newHighlightBg = adjustLightness(
    palette.highlight.background,
    DARK_DELTA
  );
  const newFocusBg = adjustLightness(palette.focus.background, DARK_DELTA);

  // Foreground contrast helpers against the new darker page background
  const txt = (fg: string) => ensureMinContrast(fg, newBg, 4.5);
  const ui = (fg: string) => ensureMinContrast(fg, newBg, 3.0);

  return {
    // Accent colors: ensure they stay readable on the now-darker background
    // (they may need a slight darkening to maintain 4.5:1 contrast)
    primary: txt(palette.primary),
    success: txt(palette.success),
    purple: txt(palette.purple),
    blue: txt(palette.blue),
    error: txt(palette.error),
    rust: txt(palette.rust),
    teal: txt(palette.teal),
    lightPurple: txt(palette.lightPurple),
    forest: txt(palette.forest),
    coral: txt(palette.coral),

    // Neutral scale: ensure readable on the new background
    dark: txt(palette.dark),
    medium: txt(palette.medium),
    light: ui(palette.light),

    // Surfaces darkened
    background: newBg,
    offWhite: newOffWhite,

    // UI states: hover/active accent backgrounds unchanged; text stays paired
    hover: {
      background: palette.hover.background,
      text: palette.hover.text,
    },
    active: {
      background: palette.active.background,
      text: palette.active.text,
    },
    disabled: {
      background: newDisabledBg,
      text: txt(palette.disabled.text),
    },

    // Text must pass on new darker background
    text: {
      primary: txt(palette.text.primary),
      secondary: txt(palette.text.secondary),
      tertiary: txt(palette.text.tertiary),
      inverse: palette.text.inverse,
    },

    // Borders must pass 3:1 UI minimum on new background
    border: {
      default: ui(palette.border.default),
      light: ui(palette.border.light),
      dark: ui(palette.border.dark),
    },

    icon: {
      primary: txt(palette.icon.primary),
      secondary: txt(palette.icon.secondary),
    },

    warning: {
      background: newWarningBg,
      border: ui(palette.warning.border),
      text: ensureMinContrast(palette.warning.text, newWarningBg, 4.5),
    },

    highlight: {
      background: newHighlightBg,
      pulse: palette.highlight.pulse,
    },

    focus: {
      ring: palette.focus.ring,
      background: newFocusBg,
    },

    reflection: {
      understood: {
        bg: adjustLightness(palette.reflection.understood.bg, DARK_DELTA),
        text: ensureMinContrast(
          palette.reflection.understood.text,
          adjustLightness(palette.reflection.understood.bg, DARK_DELTA),
          4.5
        ),
      },
      stillThinking: {
        bg: adjustLightness(palette.reflection.stillThinking.bg, DARK_DELTA),
        text: ensureMinContrast(
          palette.reflection.stillThinking.text,
          adjustLightness(palette.reflection.stillThinking.bg, DARK_DELTA),
          4.5
        ),
      },
      needHelp: {
        bg: adjustLightness(palette.reflection.needHelp.bg, DARK_DELTA),
        text: ensureMinContrast(
          palette.reflection.needHelp.text,
          adjustLightness(palette.reflection.needHelp.bg, DARK_DELTA),
          4.5
        ),
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Dark variant
// ---------------------------------------------------------------------------

/**
 * Dark variant: replace all surfaces with primary-hue-tinted near-blacks,
 * lighten accent colors so they're visible, and ensure full WCAG AA coverage
 * across the entire palette — judged against BOTH background surfaces
 * (background and offWhite / card color), since offWhite is slightly lighter.
 */
export function applyDarkVariant(palette: ColorPalette): ColorPalette {
  const [h] = hexToHSLValues(palette.primary);

  // Core surfaces — subtle branded tint per primary hue
  const darkBg = hslToHex(h, 20, 8); // page background (darkest)
  const darkCard = hslToHex(h, 15, 14); // card / offWhite (slightly lighter)
  const darkRaised = hslToHex(h, 14, 18); // raised surface (focus bg, etc.)
  const darkHoverBg = hslToHex(h, 18, 22); // hover surface

  // Accent colors: lighten by 25pts then ensure 4.5:1 against page bg
  const lightAccent = (color: string) =>
    ensureMinContrast(adjustLightness(color, 25), darkBg, 4.5);

  // Lightened primary (used for active, ring, etc.)
  const lightPrimary = lightAccent(palette.primary);

  // Disabled surface
  const disabledBg = hslToHex(h, 12, 16);
  const disabledText = ensureMinContrast('#888888', disabledBg, 4.5);

  // Border colors: neutral grays with subtle hue tint, guaranteed 3:1 on darkBg
  const borderDefault = ensureMinContrast(hslToHex(h, 12, 42), darkBg, 3.0);
  const borderLight = ensureMinContrast(hslToHex(h, 10, 32), darkBg, 3.0);
  const borderDark = ensureMinContrast(hslToHex(h, 14, 50), darkBg, 3.0);

  // Warning: fixed dark-amber palette
  const warningBg = '#2A1F0A';
  const warningBorder = ensureMinContrast('#FFA940', warningBg, 3.0);
  const warningText = ensureMinContrast('#FFD591', warningBg, 4.5);

  // Text: ensure against darkCard (offWhite), which is slightly lighter than darkBg.
  // If text passes on the lighter surface, it will also pass on the darker one.
  const ensureDarkText = (fg: string, minRatio: number) =>
    ensureMinContrast(
      ensureMinContrast(fg, darkBg, minRatio),
      darkCard,
      minRatio
    );

  const textPrimary = '#E8E8E8'; // very high contrast on both dark surfaces
  const textSecondary = '#B8B8B8'; // high contrast on both dark surfaces
  const textTertiary = ensureDarkText('#999999', 4.5);

  return {
    // Accent colors — lightened for dark background visibility
    primary: lightPrimary,
    success: lightAccent(palette.success),
    purple: lightAccent(palette.purple),
    blue: lightAccent(palette.blue),
    error: lightAccent(palette.error),
    rust: lightAccent(palette.rust),
    teal: lightAccent(palette.teal),
    lightPurple: lightAccent(palette.lightPurple),
    forest: lightAccent(palette.forest),
    coral: lightAccent(palette.coral),

    // Neutral scale — dark grays with primary hue tint
    dark: hslToHex(h, 8, 14), // darkest neutral (near background)
    medium: hslToHex(h, 6, 26), // mid neutral
    light: hslToHex(h, 5, 42), // "light" neutral in dark context

    // Surfaces
    background: darkBg,
    offWhite: darkCard,

    // UI states
    hover: {
      background: darkHoverBg,
      text: textPrimary,
    },
    active: {
      background: lightPrimary,
      text: darkBg,
    },
    disabled: {
      background: disabledBg,
      text: disabledText,
    },

    // Text — high contrast light grays, ensured against both dark surfaces
    text: {
      primary: textPrimary,
      secondary: textSecondary,
      tertiary: textTertiary,
      inverse: darkBg,
    },

    // Borders
    border: {
      default: borderDefault,
      light: borderLight,
      dark: borderDark,
    },

    // Icons
    icon: {
      primary: textPrimary,
      secondary: textSecondary,
    },

    // Warning
    warning: {
      background: warningBg,
      border: warningBorder,
      text: warningText,
    },

    // Highlight — subtle hue-tinted dark surface
    highlight: {
      background: hslToHex(h, 18, 20),
      pulse: hslToHex(h, 40, 45),
    },

    // Focus
    focus: {
      ring: lightPrimary,
      background: darkRaised,
    },

    // Reflection badge system — dark surfaces with lightened accent text
    reflection: {
      understood: {
        bg: hslToHex(
          hexToHSLValues(palette.reflection.understood.text)[0],
          40,
          12
        ),
        text: lightAccent(palette.reflection.understood.text),
      },
      stillThinking: {
        bg: hslToHex(
          hexToHSLValues(palette.reflection.stillThinking.text)[0],
          40,
          12
        ),
        text: lightAccent(palette.reflection.stillThinking.text),
      },
      needHelp: {
        bg: hslToHex(
          hexToHSLValues(palette.reflection.needHelp.text)[0],
          40,
          12
        ),
        text: lightAccent(palette.reflection.needHelp.text),
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Apply a variant to a base palette.
 * - light: returns palette unchanged
 * - medium: darken surfaces, re-check contrast across entire palette
 * - dark: full dark-mode transform with branded tint
 */
export function applyVariant(
  palette: ColorPalette,
  variant: ThemeVariant
): ColorPalette {
  switch (variant) {
    case 'medium':
      return applyMediumVariant(palette);
    case 'dark':
      return applyDarkVariant(palette);
    default:
      return palette;
  }
}
